import React, { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import ShopLayout from '../components/shop/ShopLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatPrice } from '../utils/shopHelpers';
import './AccountPage.css';
import './auth/AuthForm.css';
import Seo from '../components/seo/Seo';

function AccountNav() {
  const location = useLocation();
  const { logout, customer } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: '/account', label: 'Dashboard', end: true },
    { to: '/account/orders', label: 'Order History' },
    { to: '/wishlist', label: 'Wishlist' },
    { to: '/account/addresses', label: 'Addresses' },
    { to: '/account/profile', label: 'Profile' },
    { to: '/account/password', label: 'Change Password' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/shop');
  };

  return (
    <nav className="account-nav">
      <p style={{ padding: '8px 20px', margin: 0, fontSize: 13, color: '#6b7280' }}>
        Hello, {customer?.first_name}
      </p>
      {links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className={location.pathname === link.to || (link.end && location.pathname === '/account') ? 'active' : ''}
        >
          {link.label}
        </Link>
      ))}
      <button type="button" className="account-nav__logout" onClick={handleLogout}>
        Logout
      </button>
    </nav>
  );
}

function Dashboard() {
  const { customer } = useAuth();
  return (
    <>
      <h1>My Account</h1>
      <div className="account-card">
        <h3>Welcome back, {customer.first_name}!</h3>
        <p>Manage your orders, addresses, and account settings from here.</p>
      </div>
      <div className="account-card">
        <h3>Quick Links</h3>
        <p><Link to="/account/orders">View order history</Link> · <Link to="/shop">Continue shopping</Link></p>
      </div>
    </>
  );
}

function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getMyOrders();
        setOrders(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <h1>Order History</h1>
      {loading && <p>Loading orders…</p>}
      {!loading && orders.length === 0 && (
        <div className="account-card"><p>No orders yet. <Link to="/shop">Start shopping</Link></p></div>
      )}
      {orders.length > 0 && (
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <td>{o.order_number}</td>
                <td>{new Date(o.created_at).toLocaleDateString()}</td>
                <td>{formatPrice(o.total)}</td>
                <td><span className={`status-badge status-badge--${o.status}`}>{o.status}</span></td>
                <td><Link to={`/account/orders/${o.id}`}>View</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function OrderDetail() {
  const { id: orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getMyOrder(orderId);
        setOrder(res.data);
      } catch {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  if (loading) return <p>Loading…</p>;
  if (!order) return <p>Order not found.</p>;

  return (
    <>
      <h1>Order {order.order_number}</h1>
      <div className="account-card">
        <p><strong>Status:</strong> <span className={`status-badge status-badge--${order.status}`}>{order.status}</span></p>
        <p><strong>Payment:</strong> {order.payment_status}</p>
        <p><strong>Date:</strong> {new Date(order.created_at).toLocaleString()}</p>
        <p><strong>Total:</strong> {formatPrice(order.total)}</p>
      </div>
      {order.items?.length > 0 && (
        <table className="orders-table">
          <thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Price</th></tr></thead>
          <tbody>
            {order.items.map((item) => (
              <tr key={item.id}>
                <td>{item.product_name}</td>
                <td>{item.sku}</td>
                <td>{item.quantity}</td>
                <td>{formatPrice(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Addresses() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = {
    first_name: '', last_name: '', company: '', address_line1: '', address_line2: '',
    city: '', state: '', postal_code: '', country: 'Kenya', phone: '', is_default: false
  };
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await api.getCustomerAddresses();
      setAddresses(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.createCustomerAddress(form);
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <h1>Addresses</h1>
      {loading && <p>Loading…</p>}
      {addresses.map((a) => (
        <div key={a.id} className="account-card">
          <h3>{a.first_name} {a.last_name} {a.is_default ? '(Default)' : ''}</h3>
          <p>{a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}<br />
          {a.city}, {a.state} {a.postal_code}<br />{a.country}</p>
        </div>
      ))}
      {!showForm && (
        <button type="button" className="auth-form__submit" style={{ maxWidth: 200 }} onClick={() => setShowForm(true)}>
          Add Address
        </button>
      )}
      {showForm && (
        <form className="auth-form account-card" onSubmit={handleSave}>
          {error && <div className="auth-form__error">{error}</div>}
          <div className="auth-form__row">
            <div className="auth-form__field"><label>First Name</label><input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div className="auth-form__field"><label>Last Name</label><input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div className="auth-form__field"><label>Address Line 1</label><input required value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></div>
          <div className="auth-form__field"><label>City</label><input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div className="auth-form__row">
            <div className="auth-form__field"><label>Country</label><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
            <div className="auth-form__field"><label>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <label className="auth-form__checkbox"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Set as default</label>
          <button type="submit" className="auth-form__submit">Save Address</button>
        </form>
      )}
    </>
  );
}

function Profile() {
  const { customer, updateCustomer } = useAuth();
  const [form, setForm] = useState({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    phone: customer?.phone || '',
    company: customer?.company || '',
    newsletter: customer?.newsletter || false
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const res = await api.updateCustomerProfile(form);
      updateCustomer(res.data);
      setMessage('Profile updated');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <h1>Profile Details</h1>
      <form className="auth-form account-card" onSubmit={handleSave}>
        {error && <div className="auth-form__error">{error}</div>}
        {message && <div className="auth-form__success">{message}</div>}
        <div className="auth-form__field"><label>Email</label><input value={customer.email} disabled /></div>
        <div className="auth-form__row">
          <div className="auth-form__field"><label>First Name</label><input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
          <div className="auth-form__field"><label>Last Name</label><input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
        </div>
        <div className="auth-form__row">
          <div className="auth-form__field"><label>Phone</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="auth-form__field"><label>Company</label><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
        </div>
        <button type="submit" className="auth-form__submit">Save Changes</button>
      </form>
    </>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    try {
      await api.changeCustomerPassword({ current_password: form.current_password, new_password: form.new_password });
      setMessage('Password updated successfully');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <h1>Change Password</h1>
      <form className="auth-form account-card" onSubmit={handleSave}>
        {error && <div className="auth-form__error">{error}</div>}
        {message && <div className="auth-form__success">{message}</div>}
        <div className="auth-form__field"><label>Current Password</label><input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} /></div>
        <div className="auth-form__field"><label>New Password</label><input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} /></div>
        <div className="auth-form__field"><label>Confirm New Password</label><input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></div>
        <button type="submit" className="auth-form__submit">Update Password</button>
      </form>
    </>
  );
}

function AccountPage() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <ShopLayout breadcrumbs={[{ label: 'My Account' }]}>
        <Seo title="My Account" path="/account" />
        <div className="container" style={{ padding: 48 }}>Loading…</div>
      </ShopLayout>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <ShopLayout breadcrumbs={[{ label: 'My Account' }]}>
      <Seo title="My Account" path="/account" />
      <div className="container account-layout">
        <AccountNav />
        <div className="account-content">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<OrderHistory />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="addresses" element={<Addresses />} />
            <Route path="profile" element={<Profile />} />
            <Route path="password" element={<ChangePassword />} />
          </Routes>
        </div>
      </div>
    </ShopLayout>
  );
}

export default AccountPage;
